import { OnvoDashboardDatasources } from "../dashboard_datasources/index.js";
import OnvoBase, { JobUpdateEvent } from "../base.js";
import { Dashboard } from "../types/dashboard.js";

export class OnvoDashboard extends OnvoBase {
  datasources: OnvoDashboardDatasources;
  #id: string;

  constructor(id: string, apiKey: string, options?: { endpoint: string }) {
    super(apiKey, options);
    this.#id = id;
    this.datasources = new OnvoDashboardDatasources(id, apiKey, options);
  }

  /**
   * Updates the widget cache for the dashboard.
   *
   * @return {Promise<{success: boolean, id: string}>} A promise that resolves to an object containing the success and id of the widget.
   */
  updateCache(callback?: (event: JobUpdateEvent) => void, rejectCache?: boolean) {
    return this._fetchStreamBase(
      "/api/dashboards/" + this.#id + "/update-cache" + (rejectCache ? "?rejectCache=true" : ""),
      "POST",
      undefined,
      callback
    ) as Promise<Dashboard & { _meta: { status: string, url: string, total?: number, completed?: number } }>;
  }

  /**
   * Exports the dashboard in the specified format.
   *
   * @param {("csv" | "xlsx" | "pdf" | "png" | "jpeg")} format - The format to export the dashboard in.
   * @return {Promise<Blob>} A promise that resolves to a Blob representing the exported dashboard.
   */
  export(format: "csv" | "xlsx" | "pdf" | "png" | "jpeg" | "pptx", theme: "light" | "dark" = "light", tab: number = 0, onProgress?: (event: JobUpdateEvent) => void) {
    return this._fetchStreamBase(
      `/api/dashboards/${this.#id}/export?theme=${theme}&format=${format}&tab=${tab}`,
      "GET",
      undefined,
      onProgress
    );
  }

  /**
 * Duplicates the dashboard.
 *
 * @return {Promise<{id: string}>} A promise that resolves to an object containing the id of the duplicated dashboard.
 */
  duplicate(): Promise<{ id: string }> {
    return this._fetchBase(`/api/dashboards/${this.#id}/duplicate`, "POST");
  }

  /**
   * Summarizes the dashboard using the provided prompt.
   *
   * @param {string} prompt - The prompt to use for summarizing the dashboard.
   * @return {Promise<{title: string, description: string}>} A promise that resolves to an object containing the title and description of the summarized dashboard.
   */
  summarize(prompt: string): Promise<{ title: string; description: string }> {
    return this._fetchBase(`/api/dashboards/${this.#id}/summarize`, "POST", {
      prompt,
    });
  }

  /**
   * Synchronizes the dashboard with the latest data and settings.
   *
   * @param {Object} [options] - Optional parameters for synchronization
   * @param {boolean} [options.settings] - Whether to include settings in the sync
   * @param {boolean} [options.widgets] - Whether to include widgets in the sync
   * @param {boolean} [options.datasources] - Whether to include datasources in the sync
   * @return {Promise<any>} A promise that resolves with the sync result
   */
  sync(options?: { settings?: boolean; widgets?: boolean; datasources?: boolean }): Promise<any> {
    let includeString = "";
    if (options) {
      const items = Object.entries(options)
        .filter(([_, value]) => value)
        .map(([key]) => key)
        .join(",");
      if (items) {
        includeString = `?include=${items}`;
      }
    }
    return this._fetchBase(`/api/dashboards/${this.#id}/sync${includeString}`, "POST");
  }
}
